import React from "react";
import { FormControl, Option, Sheet, Switch, Table } from "@mui/joy";
import { SSave } from "../../../../styles/StyledMUI";
import { hrAccentColor, hrSheetSx, hrTableSx, textTokens } from "../../../../styles/globalStyles";
import DarkCard from "../../../common/DarkCard";
import DarkInput from "../../../common/DarkInput";
import DarkSelect from "../../../common/DarkSelect";
import DarkTypography from "../../../common/DarkTypography";
import IconButtonCTA from "../../../common/IconButtonCTA";
import {
  DEFAULT_TAX_RATE,
  TAX_RATES,
  formatNum,
  htToTtc,
  ttcToHt,
} from "../../shared/billingUtils";
import type {
  RentalModelPricing,
  RentalModelPricingUpdate,
} from "../../../../api/rental/Rental.typed";

type Props = {
  rows: RentalModelPricing[];
  canEdit: boolean;
  onSave: (modelID: number, payload: RentalModelPricingUpdate) => Promise<unknown>;
};

type RowDraft = {
  enabled: boolean;
  priceHT: string;
  priceTTC: string;
  taxRate: number;
  currency: string;
};

function rowDraftFrom(row: RentalModelPricing): RowDraft {
  const taxRate = row.rental_tax_rate ?? DEFAULT_TAX_RATE;
  const priceHT = row.rental_price_ht;

  return {
    enabled: row.is_rentable_per_unit,
    priceHT: priceHT != null ? formatNum(priceHT) : "",
    priceTTC: priceHT != null ? formatNum(htToTtc(priceHT, taxRate)) : "",
    taxRate,
    currency: row.rental_currency ?? "EUR",
  };
}

export default function RentalUnitPricingCard({ rows, canEdit, onSave }: Props) {
  const [drafts, setDrafts] = React.useState<Record<number, RowDraft>>({});

  React.useEffect(() => {
    const next: Record<number, RowDraft> = {};
    rows.forEach((r) => {
      next[r.model_id] = rowDraftFrom(r);
    });
    setDrafts(next);
  }, [rows]);

  const update = (modelID: number, patch: Partial<RowDraft>) =>
    setDrafts((prev) => ({ ...prev, [modelID]: { ...prev[modelID], ...patch } }));

  const toggleEnabled = (modelID: number, enabled: boolean) => update(modelID, { enabled });

  const setHT = (modelID: number, raw: string) => {
    const current = drafts[modelID];
    if (!current) return;
    const parsed = parseFloat(raw);
    const ttc = Number.isNaN(parsed) ? "" : formatNum(htToTtc(parsed, current.taxRate));
    update(modelID, { priceHT: raw, priceTTC: ttc });
  };

  const setTTC = (modelID: number, raw: string) => {
    const current = drafts[modelID];
    if (!current) return;
    const parsed = parseFloat(raw);
    const ht = Number.isNaN(parsed) ? "" : formatNum(ttcToHt(parsed, current.taxRate));
    update(modelID, { priceTTC: raw, priceHT: ht });
  };

  const setTax = (modelID: number, rate: number) => {
    const current = drafts[modelID];
    if (!current) return;
    const parsedHT = parseFloat(current.priceHT);
    const ttc = Number.isNaN(parsedHT) ? current.priceTTC : formatNum(htToTtc(parsedHT, rate));
    update(modelID, { taxRate: rate, priceTTC: ttc });
  };

  const handleSave = async (row: RentalModelPricing) => {
    const draft = drafts[row.model_id];
    if (!draft) return;

    const parsed = parseFloat(draft.priceHT);
    const priceHT = !Number.isNaN(parsed) ? parsed : null;

    await onSave(row.model_id, {
      is_rentable_per_unit: draft.enabled,
      rental_price_ht: priceHT,
      rental_tax_rate: priceHT != null ? draft.taxRate : null,
      rental_currency: priceHT != null ? draft.currency : null,
    });
  };

  return (
    <DarkCard dense>
      <DarkTypography level="title-md" sx={{ mb: 0.5, color: hrAccentColor }}>
        Tarifs unitaires par modèle
        <DarkTypography
          component="span"
          level="body-sm"
          sx={{ color: textTokens.color.faint, ml: 1 }}
        >
          (location à l'unité, sans kit)
        </DarkTypography>
      </DarkTypography>
      <Sheet sx={hrSheetSx}>
        <Table hoverRow sx={hrTableSx}>
          <thead>
            <tr>
              <th>Modèle</th>
              <th style={{ width: 100 }}>Louable</th>
              <th style={{ width: 140 }}>Tarif jour HT</th>
              <th style={{ width: 140 }}>Tarif jour TTC</th>
              <th style={{ width: 120 }}>TVA</th>
              <th style={{ width: 120 }}>Devise</th>
              {canEdit && <th style={{ width: 70 }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={canEdit ? 7 : 6}>
                  <DarkTypography level="body-sm" colorVariant="muted">
                    Aucun modèle d'équipement.
                  </DarkTypography>
                </td>
              </tr>
            )}
            {rows.map((row) => {
              const draft = drafts[row.model_id] ?? rowDraftFrom(row);

              return (
                <tr key={row.model_id}>
                  <td>{row.model_name}</td>
                  <td>
                    <Switch
                      checked={draft.enabled}
                      disabled={!canEdit}
                      onChange={(e) => toggleEnabled(row.model_id, e.target.checked)}
                    />
                  </td>
                  <td>
                    <FormControl>
                      <DarkInput
                        type="number"
                        placeholder="—"
                        slotProps={{ input: { step: "0.01", min: "0" } }}
                        value={draft.priceHT}
                        disabled={!canEdit}
                        onChange={(e) => setHT(row.model_id, e.target.value)}
                      />
                    </FormControl>
                  </td>
                  <td>
                    <FormControl>
                      <DarkInput
                        type="number"
                        placeholder="—"
                        slotProps={{ input: { step: "0.01", min: "0" } }}
                        value={draft.priceTTC}
                        disabled={!canEdit}
                        onChange={(e) => setTTC(row.model_id, e.target.value)}
                      />
                    </FormControl>
                  </td>
                  <td>
                    <FormControl>
                      <DarkSelect
                        value={draft.taxRate}
                        disabled={!canEdit}
                        onChange={(_, v) => v != null && setTax(row.model_id, v as number)}
                      >
                        {TAX_RATES.map((r) => (
                          <Option key={r.value} value={r.value}>
                            {r.label}
                          </Option>
                        ))}
                      </DarkSelect>
                    </FormControl>
                  </td>
                  <td>
                    <FormControl>
                      <DarkSelect
                        value={draft.currency}
                        disabled={!canEdit}
                        onChange={(_, v) =>
                          v != null && update(row.model_id, { currency: v as string })
                        }
                      >
                        <Option value="EUR">EUR</Option>
                        <Option value="USD">USD</Option>
                        <Option value="GBP">GBP</Option>
                        <Option value="CHF">CHF</Option>
                      </DarkSelect>
                    </FormControl>
                  </td>
                  {canEdit && (
                    <td>
                      <IconButtonCTA
                        aria-label="Enregistrer le tarif"
                        size="sm"
                        ctaVariant="success"
                        onClick={() => handleSave(row)}
                      >
                        <SSave />
                      </IconButtonCTA>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Sheet>
    </DarkCard>
  );
}
